/**
 * Client half of the WAE Contentful connector.
 *
 * The connector's `request` edgehancer returns the Contentful REST collection
 * shape and echoes the EFFECTIVE query it posted under
 * `extensions.wae = { query, variables }` (see `request.ts` in
 * wac-management-service). The browser replays that same query with `skip`
 * advanced, so page two carries the identical filter, sort and selection set as
 * the server-rendered page one.
 *
 * Nothing here knows a content type. A new gallery costs a data type, not code.
 */

/** GraphQL variables the edgehancer posted, and the browser re-posts. */
export type ConnectorVariables = {
	limit?: number;
	skip?: number;
	locale?: string;
	where?: Record<string, unknown>;
	order?: string[];
};

/** The `extensions.wae` echo: the query string plus the variables it ran with. */
export type ConnectorQueryEcho = {
	query: string;
	variables: ConnectorVariables;
};

/** One fetched page, already reshaped into the Contentful REST entry shape. */
export type ConnectorPage = {
	items: unknown[];
	/** Total matching entries, from the collection node. Never inferred. */
	total: number;
	/** The offset this page started at. */
	skip: number;
};

/** Runs one echo against Contentful. The transport, injectable for tests. */
export type RunConnectorQuery = (echo: ConnectorQueryEcho) => Promise<ConnectorPage>;

/** One page of items plus the echo that fetches the next page, or null at the end. */
export type ConnectorResource = {
	items: unknown[];
	query: ConnectorQueryEcho | null;
};

/** Fetches a page and returns the next-page echo. Owns the pagination math. */
export type ExecuteConnectorQuery = (echo: ConnectorQueryEcho) => Promise<ConnectorResource>;

/** Reads the echo off a resolved data resource, tolerating a missing one. */
export const readQueryEcho = (value: unknown): ConnectorQueryEcho | null => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
	const candidate = value as { query?: unknown; variables?: unknown };
	if (typeof candidate.query !== 'string' || !candidate.query.trim()) return null;
	const variables =
		candidate.variables &&
		typeof candidate.variables === 'object' &&
		!Array.isArray(candidate.variables)
			? (candidate.variables as ConnectorVariables)
			: {};
	return { query: candidate.query, variables };
};
